import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Attendance from './pages/Attendance';
import Staff from './pages/Staff';
import Leave from './pages/Leave';
import Salary from './pages/Salary';
import Reports from './pages/Reports';
import Holidays from './pages/Holidays';
import Overtime from './pages/Overtime';
import Announcements from './pages/Announcements';

const Departments        = lazy(() => import('./pages/Departments'));
const LeaveBalance       = lazy(() => import('./pages/LeaveBalance'));
const LoanAdvance        = lazy(() => import('./pages/LoanAdvance'));
const Disciplinary       = lazy(() => import('./pages/Disciplinary'));
const Performance        = lazy(() => import('./pages/Performance'));
const Training           = lazy(() => import('./pages/Training'));
const Recruitment        = lazy(() => import('./pages/Recruitment'));
const Onboarding         = lazy(() => import('./pages/Onboarding'));
const Assets             = lazy(() => import('./pages/Assets'));
const Shifts             = lazy(() => import('./pages/Shifts'));
const SalaryRevisions    = lazy(() => import('./pages/SalaryRevisions'));
const PayGrades          = lazy(() => import('./pages/PayGrades'));
const Grievances         = lazy(() => import('./pages/Grievances'));
const ExitManagement     = lazy(() => import('./pages/ExitManagement'));
const Policies           = lazy(() => import('./pages/Policies'));
const Company            = lazy(() => import('./pages/Company'));
const Insurance          = lazy(() => import('./pages/Insurance'));
const Transfer           = lazy(() => import('./pages/Transfer'));
const HrHelpdesk         = lazy(() => import('./pages/HrHelpdesk'));
const HrReports          = lazy(() => import('./pages/HrReports'));
const BirthdayAnniversary= lazy(() => import('./pages/BirthdayAnniversary'));
const Reimbursement      = lazy(() => import('./pages/Reimbursement'));
const Chat               = lazy(() => import('./pages/Chat'));
const Notifications      = lazy(() => import('./pages/Notifications'));
const AdvancedReports    = lazy(() => import('./pages/AdvancedReports'));
const Form16             = lazy(() => import('./pages/Form16'));
const StatutoryCompliance = lazy(() => import('./pages/StatutoryCompliance'));
const EmployeeKundali    = lazy(() => import('./pages/EmployeeKundali'));
const PendingRequests    = lazy(() => import('./pages/PendingRequests'));
const ManagerInbox       = lazy(() => import('./pages/ManagerInbox'));
const Permissions        = lazy(() => import('./pages/Permissions'));

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

// Admin-only route — staff gets redirected to dashboard
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'ADMIN') return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
}

// Admin + Manager route — MANAGER can also access (e.g. employee kundali)
function AdminOrManagerRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'ADMIN' && user.role !== 'MANAGER') return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
}

const Loading = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748b', fontSize: 14 }}>
    Loading...
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          style: { fontFamily: "'Inter', 'Segoe UI', sans-serif", fontSize: 14 }
        }} />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Common routes — visible to both ADMIN and STAFF (pages handle data filtering) */}
            <Route path="/dashboard"     element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/attendance"    element={<PrivateRoute><Attendance /></PrivateRoute>} />
            <Route path="/leave"         element={<PrivateRoute><Leave /></PrivateRoute>} />
            <Route path="/leave-balance" element={<PrivateRoute><LeaveBalance /></PrivateRoute>} />
            <Route path="/overtime"      element={<PrivateRoute><Overtime /></PrivateRoute>} />
            <Route path="/holidays"      element={<PrivateRoute><Holidays /></PrivateRoute>} />
            <Route path="/announcements" element={<PrivateRoute><Announcements /></PrivateRoute>} />
            <Route path="/grievances"    element={<PrivateRoute><Grievances /></PrivateRoute>} />
            <Route path="/salary"        element={<PrivateRoute><Salary /></PrivateRoute>} />
            <Route path="/loans"         element={<PrivateRoute><LoanAdvance /></PrivateRoute>} />
            <Route path="/training"      element={<PrivateRoute><Training /></PrivateRoute>} />
            <Route path="/performance"   element={<PrivateRoute><Performance /></PrivateRoute>} />
            <Route path="/policies"      element={<PrivateRoute><Policies /></PrivateRoute>} />
            <Route path="/assets"        element={<PrivateRoute><Assets /></PrivateRoute>} />
            <Route path="/disciplinary"  element={<PrivateRoute><Disciplinary /></PrivateRoute>} />

            {/* Common routes (Admin + Staff) — new v2 modules */}
            <Route path="/helpdesk"          element={<PrivateRoute><HrHelpdesk /></PrivateRoute>} />
            <Route path="/reimbursements"    element={<PrivateRoute><Reimbursement /></PrivateRoute>} />
            <Route path="/chat"              element={<PrivateRoute><Chat /></PrivateRoute>} />
            <Route path="/notifications"     element={<PrivateRoute><Notifications /></PrivateRoute>} />

            {/* Admin-only routes */}
            <Route path="/staff"              element={<AdminRoute><Staff /></AdminRoute>} />
            <Route path="/staff/:id/kundali"  element={<AdminOrManagerRoute><EmployeeKundali /></AdminOrManagerRoute>} />
            <Route path="/pending-requests"   element={<AdminRoute><PendingRequests /></AdminRoute>} />
            <Route path="/manager-inbox"      element={<PrivateRoute><ManagerInbox /></PrivateRoute>} />
            <Route path="/departments"        element={<AdminRoute><Departments /></AdminRoute>} />
            <Route path="/salary-revisions"   element={<AdminRoute><SalaryRevisions /></AdminRoute>} />
            <Route path="/pay-grades"         element={<AdminRoute><PayGrades /></AdminRoute>} />
            <Route path="/reports"            element={<AdminRoute><Reports /></AdminRoute>} />
            <Route path="/advanced-reports"   element={<AdminRoute><AdvancedReports /></AdminRoute>} />
            <Route path="/recruitment"        element={<AdminRoute><Recruitment /></AdminRoute>} />
            <Route path="/onboarding"         element={<AdminRoute><Onboarding /></AdminRoute>} />
            <Route path="/shifts"             element={<AdminRoute><Shifts /></AdminRoute>} />
            <Route path="/exit-management"    element={<AdminRoute><ExitManagement /></AdminRoute>} />
            <Route path="/company"            element={<AdminRoute><Company /></AdminRoute>} />
            <Route path="/insurance"          element={<AdminRoute><Insurance /></AdminRoute>} />
            <Route path="/transfers"          element={<AdminRoute><Transfer /></AdminRoute>} />
            <Route path="/hr-reports"         element={<AdminRoute><HrReports /></AdminRoute>} />
            <Route path="/form16"             element={<AdminRoute><Form16 /></AdminRoute>} />
            <Route path="/statutory"          element={<AdminRoute><StatutoryCompliance /></AdminRoute>} />
            <Route path="/birthday-anniversary" element={<AdminRoute><BirthdayAnniversary /></AdminRoute>} />
            <Route path="/permissions"          element={<AdminRoute><Permissions /></AdminRoute>} />

            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;